import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { AppIcon } from '@/components/icons/AppIcon';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signIn, loading: authLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Email e password sono obbligatori');
      return;
    }

    setLoading(true);

    try {
      await signIn(email, password);
      // Navigation will happen automatically via useEffect
    } catch (err: any) {
      console.error('Login error:', err);

      // Handle specific Firebase errors
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Email o password non corretti');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Troppi tentativi. Riprova più tardi');
      } else if (err.code === 'auth/user-disabled') {
        setError('Account sospeso. Contatta l\'amministratore');
      } else if (err.message?.includes('suspended')) {
        setError('Account sospeso. Contatta l\'amministratore');
      } else if (err.message?.includes('pending')) {
        setError('Account non ancora attivato. Controlla la tua email per completare la registrazione');
      } else {
        setError('Errore durante il login. Riprova');
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#95102d]">
        <Loader2 className="w-8 h-8 animate-spin text-wine-parchment" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#95102d] p-4 relative overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-block mx-auto"
          >
            <AppIcon className="w-20 h-20" />
          </motion.div>
          <h1 className="text-5xl font-serif font-bold text-[rgb(255,239,226)] mb-2 tracking-wide">MyWine</h1>
          <p className="text-wine-cork text-lg">La Tua Cantina Digitale</p>
        </div>

        {/* Login Card */}
        <Card className="bg-wine-parchment border-wine-cork">
          <CardHeader className="border-wine-cork">
            <CardTitle className="text-wine-barrel">Accedi al tuo account</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Alert */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"
                >
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </motion.div>
              )}

              {/* Email Field */}
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@esempio.com"
                icon={Mail}
                required
                autoComplete="email"
              />

              {/* Password Field */}
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                icon={Lock}
                showPasswordToggle
                required
                autoComplete="current-password"
              />

              {/* Submit Button */}
              <Button type="submit" fullWidth loading={loading} liquidEffect>
                Accedi
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-wine-parchment">
            Non hai un account? Contatta l'amministratore per ricevere un invito.
          </p>
        </div>

        {/* App Version */}
        <div className="mt-8 text-center">
          <p className="text-xs text-wine-cork/80">MyWine v1.0.0</p>
        </div>
      </motion.div>
    </div>
  );
};
